import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Trash2, UserMinus } from "lucide-react";
import { errMsg, fmtPts } from "../../lib/api";
import { adminApi } from "../../lib/adminApi";
import { AdminTournamentForm } from "./AdminTournamentForm";
import { AdjustmentDialog } from "../AdjustmentDialog";

export const AdminTournamentDetail = ({ tid, onBack }) => {
  const [t, setT] = useState(null);
  const load = useCallback(() => adminApi.get(`/tournaments/${tid}`).then((r) => setT(r.data)).catch((e) => toast.error(errMsg(e))), [tid]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Eliminare "${t.name}" per tutti i giocatori?`)) return;
    try { await adminApi.delete(`/tournaments/${tid}`); toast.success("Torneo eliminato"); onBack(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const [editing, setEditing] = useState(null);
  const remove = (r) => window.confirm(`Rimuovere ${r.name}?`) && act(() => adminApi.delete(`/tournaments/${tid}/members/${r.user_id}`), "Giocatore rimosso");

  if (!t) return <div className="card-surface h-40 animate-pulse" />;
  return (
    <div className="space-y-6" data-testid="admin-tournament-detail">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => onBack(false)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="admin-back-button"><ArrowLeft size={16} /> Tornei</button>
        <button onClick={del} data-testid="admin-delete-tournament-button" className="h-9 px-3 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-1.5 text-xs"><Trash2 size={14} /> Elimina torneo</button>
      </div>
      <h2 className="font-display text-3xl font-black uppercase">{t.name} <span className="text-sm text-slate-400 font-mono">{t.competition_name} · {t.members_count} giocatori</span></h2>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface overflow-x-auto" data-testid="admin-leaderboard">
          <table className="w-full text-sm">
            <thead><tr className="text-left font-mono text-[10px] uppercase tracking-widest text-slate-400 border-b border-[#26334D]">
              <th className="px-3 py-2">#</th><th className="px-3 py-2">Giocatore</th><th className="px-3 py-2 text-right">Punti</th><th className="px-3 py-2 text-right">Esatti</th><th className="px-3 py-2 text-right">Bonus</th><th className="px-3 py-2 text-right">Ingresso</th><th className="px-3 py-2 text-right">Rettifica</th><th className="px-3 py-2" />
            </tr></thead>
            <tbody>
              {t.leaderboard.length === 0 && <tr><td colSpan={8} className="px-3 py-4 text-slate-500">Nessun iscritto.</td></tr>}
              {t.leaderboard.map((r) => (
                <tr key={r.user_id} className="border-b border-[#26334D]/60" data-testid={`admin-row-${r.user_id}`}>
                  <td className="px-3 py-2 font-display font-black">{r.rank}</td>
                  <td className="px-3 py-2 font-semibold">{r.name}</td>
                  <td className="px-3 py-2 text-right font-display font-black text-emerald-400">{fmtPts(r.points)}</td>
                  <td className="px-3 py-2 text-right text-violet-400">{r.exact}</td>
                  <td className="px-3 py-2 text-right text-amber-400">{r.bonuses}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{fmtPts(r.entry_points)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditing(r)} className="text-amber-400 hover:underline" data-testid={`admin-adjust-${r.user_id}`}>{fmtPts(r.adjustment)}</button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => remove(r)} className="text-slate-400 hover:text-red-400" title="Rimuovi" data-testid={`admin-remove-${r.user_id}`}><UserMinus size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <AdjustmentDialog tid={tid} row={editing} onClose={() => setEditing(null)} onSaved={load} client={adminApi} />
        </div>
        <div className="card-surface p-5">
          <h3 className="font-display text-xl font-bold uppercase mb-4">Modifica torneo</h3>
          <AdminTournamentForm initial={t} onSaved={load} />
        </div>
      </div>
    </div>
  );
};
